import { RouteComponentProps, useHistory } from "react-router";
import { useI18n } from "i18n";
import { AppBarContainer } from "modules/app/menu/components/AppBarContainer";
import { IHistoryLocation } from "types/history";
import { ManageEventContextProvider } from "modules/manageEvent";
import { PAGES, usePageTracking } from "modules/tracking";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectBrandName } from "modules/settings/redux/selectors";
import { getRemoHosts } from "config/getRemoHost";
import logo from "modules/event/assets/logo.svg";
import { ManageEventActions } from "..";
import ManageEvent from "../ManageEvent";
import { styles } from "./styles";

type Props = RouteComponentProps<{ eventId?: string }>;

const ManageEventPage = ({
  match: {
    params: { eventId },
  },
  location: { search },
}: Props): JSX.Element => {
  const history = useHistory();
  const { t } = useI18n(["manageEvent", "event"]);

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const isRemoDomain = getRemoHosts().includes(window.location.host);
  const brandName = useSelector(selectBrandName);

  const { location } = useHistory<IHistoryLocation>();
  const preDefinedEventData = location.state && location.state.event;
  const isUnlimitedEvent =
    (location.state && location.state.isUnlimitedEvent) ||
    location.state?.event?.isUnlimitedEvent;

  const title = useMemo(() => {
    if (query.get("clone")) {
      return t("manageEvent:cloning.event");
    }
    if (eventId) {
      return isUnlimitedEvent
        ? t("manageEvent:edit.space")
        : t("manageEvent:edit.event");
    }
    return isUnlimitedEvent
      ? t("manageEvent:create.new.space")
      : t("manageEvent:create.new.event");
  }, [eventId, isUnlimitedEvent, query, t]);

  const pageName = useMemo(() => {
    if (query.get("clone")) {
      return PAGES.MANAGE_EVENT_CLONE;
    }
    return eventId ? PAGES.MANAGE_EVENT_UPDATE : PAGES.MANAGE_EVENT_CREATE;
  }, [eventId, query]);

  usePageTracking(pageName, {
    metadata: {
      eventId,
    },
  });

  const handleSave = () => {
    history.push("/my-events");
  };

  return (
    <ManageEventContextProvider
      eventId={eventId}
      preDefinedEventData={{ ...preDefinedEventData, isUnlimitedEvent }}
    >
      <AppBarContainer
        title={
          isRemoDomain
            ? t("event:welcome.to.remo")
            : t("event:welcome.to.custom.domain", {
                companyName: brandName,
              })
        }
        logoImage={<img src={logo} alt="logo" style={styles.logo} />}
      />
      <AppBarContainer
        title={title}
        containerStyles={styles.appBarContainer}
        hideEdcApps
        showEventsBlueDot
        rightAdornment={
          <ManageEventActions
            onSuccessfulSave={handleSave}
            closeButtonLink="/my-events"
          />
        }
      />
      <ManageEvent />
    </ManageEventContextProvider>
  );
};

export default ManageEventPage;
